import { addDays, parse, isValid } from 'date-fns';
import type { Caregiver, CalendarEvent } from '../types/data.types';

/**
 * Search caregivers by name, city, phone, or email
 */
export function searchCaregivers(query: string, caregivers: Caregiver[]): Caregiver[] {
  const normalized = query.toLowerCase().trim();

  if (!normalized) return [];

  const filtered = caregivers.filter(cg => {
    const nameMatch = cg.fullName.toLowerCase().includes(normalized);
    const cityMatch = cg.city.toLowerCase().includes(normalized);
    const phoneMatch = cg.phone?.includes(normalized);
    const emailMatch = cg.email?.toLowerCase().includes(normalized);

    return nameMatch || cityMatch || phoneMatch || emailMatch;
  });

  const sorted = filtered.sort((a, b) => {
    const aName = a.fullName.toLowerCase();
    const bName = b.fullName.toLowerCase();

    // Exact match first
    if (aName === normalized) return -1;
    if (bName === normalized) return 1;

    // Starts with query
    if (aName.startsWith(normalized) && !bName.startsWith(normalized)) return -1;
    if (bName.startsWith(normalized) && !aName.startsWith(normalized)) return 1;

    // Alphabetical
    return aName.localeCompare(bName);
  });

  return sorted.slice(0, 5);
}

/**
 * Search assignments by patient name or care plan ID
 */
export function searchAssignments(query: string, events: CalendarEvent[]): CalendarEvent[] {
  const normalized = query.toLowerCase().trim();

  if (!normalized) return [];

  const now = new Date();
  now.setHours(0, 0, 0, 0); // Start of today

  return events
    .filter(event =>
      event.type === 'assignment' &&
      (event.title.toLowerCase().includes(normalized) ||
       event.carePlanId?.toLowerCase().includes(normalized))
    )
    .sort((a, b) => {
      const aDate = new Date(a.startDate);
      const bDate = new Date(b.startDate);
      aDate.setHours(0, 0, 0, 0);
      bDate.setHours(0, 0, 0, 0);

      const aIsFuture = aDate >= now;
      const bIsFuture = bDate >= now;

      // Future assignments come before past assignments
      if (aIsFuture && !bIsFuture) return -1;
      if (!aIsFuture && bIsFuture) return 1;

      // Within future: earliest first (chronological)
      if (aIsFuture && bIsFuture) {
        return aDate.getTime() - bDate.getTime();
      }

      // Within past: most recent first (reverse chronological)
      return bDate.getTime() - aDate.getTime();
    })
    .slice(0, 20); // Max 20 results - users often search for patient assignments across time
}

/**
 * Parse natural language date queries
 */
export function parseDateQuery(query: string): Date[] {
  const results: Date[] = [];
  const normalized = query.toLowerCase().trim();

  if (!normalized) return results;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Today
  if (normalized === 'today') {
    results.push(new Date(today));
    return results;
  }

  // Tomorrow
  if (normalized === 'tomorrow') {
    results.push(addDays(today, 1));
    return results;
  }

  // Month name patterns
  const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'];
  const monthShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  // Try "jan 15" or "january 15"
  const monthDayPattern = /^(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)\s+(\d{1,2})$/;
  let match = normalized.match(monthDayPattern);
  if (match) {
    const monthStr = match[1];
    const day = parseInt(match[2], 10);
    const monthIndex = monthNames.indexOf(monthStr);
    const monthShortIndex = monthShort.indexOf(monthStr);
    const month = monthIndex >= 0 ? monthIndex : monthShortIndex;

    if (month >= 0 && day >= 1 && day <= 31) {
      const date = new Date(today.getFullYear(), month, day);
      if (isValid(date)) {
        results.push(date);
        return results;
      }
    }
  }

  // Try "15 jan" or "15 january"
  const dayMonthPattern = /^(\d{1,2})\s+(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|september|oct|october|nov|november|dec|december)$/;
  match = normalized.match(dayMonthPattern);
  if (match) {
    const day = parseInt(match[1], 10);
    const monthStr = match[2];
    const monthIndex = monthNames.indexOf(monthStr);
    const monthShortIndex = monthShort.indexOf(monthStr);
    const month = monthIndex >= 0 ? monthIndex : monthShortIndex;

    if (month >= 0 && day >= 1 && day <= 31) {
      const date = new Date(today.getFullYear(), month, day);
      if (isValid(date)) {
        results.push(date);
        return results;
      }
    }
  }

  // Try ISO format "2026-01-15"
  const isoPattern = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
  match = normalized.match(isoPattern);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const date = new Date(year, month, day);
    if (isValid(date)) {
      results.push(date);
      return results;
    }
  }

  // Try MM/DD/YYYY or DD/MM/YYYY
  const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  match = normalized.match(slashPattern);
  if (match) {
    const first = parseInt(match[1], 10);
    const second = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Try MM/DD/YYYY (US format)
    const usDate = new Date(year, first - 1, second);
    if (isValid(usDate)) {
      results.push(usDate);
    }

    // Try DD/MM/YYYY (International format) if different
    if (first !== second) {
      const intlDate = new Date(year, second - 1, first);
      if (isValid(intlDate) && intlDate.getTime() !== usDate.getTime()) {
        results.push(intlDate);
      }
    }
  }

  return results.slice(0, 3); // Max 3 date results
}

/**
 * Search care plans by ID and group events
 */
export function searchCarePlans(
  query: string,
  events: CalendarEvent[]
): Array<{ carePlanId: string; events: CalendarEvent[] }> {
  const normalized = query.toLowerCase().trim();

  if (!normalized) return [];

  const carePlanMap = new Map<string, CalendarEvent[]>();

  events
    .filter(event =>
      event.carePlanId?.toLowerCase().includes(normalized)
    )
    .forEach(event => {
      if (event.carePlanId) {
        const existing = carePlanMap.get(event.carePlanId) || [];
        carePlanMap.set(event.carePlanId, [...existing, event]);
      }
    });

  return Array.from(carePlanMap.entries())
    .map(([carePlanId, events]) => ({
      carePlanId,
      events: events.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    }))
    .slice(0, 3); // Max 3 care plan results
}
